import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";
import { useReducedMotion } from "motion/react";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";

import { TopHeaderCalendar } from "@ryde/controls";
import { PageHeader } from "@ryde/layout";
import {
  PageHeaderContext,
  useDashboardActions,
  usePageContext,
} from "@ryde/redux";
import { topCalendarDatesAtomFamily, TopCalendarMode } from "@ryde/shared";

import {
  dashboardAppliedFiltersAtom,
  dashboardCardsAtom,
} from "./dynamic-dashboard.atoms";
import { DASHBOARD_GRID_APPEAR_VARIANTS } from "./dynamic-dashboard.constants";
import { DASHBOARD_ENTITY } from "./dynamic-dashboard.models";
import { DashboardGridStyled } from "./dynamic-dashboard.styled";
import { buildDashboardFetchQuery } from "./hooks/dashboard-fetch-query.utils";
import { useDashboardFilters } from "./hooks/use-dashboard-filters";
import { useDashboardGridAppear } from "./hooks/use-dashboard-grid-appear";
import { useDashboardViews } from "./hooks/use-dashboard-views";
import { useIsDashboardLayoutResolved } from "./hooks/use-is-dashboard-layout-resolved";
import {
  DashboardFilterBar,
  DynamicDashboardCard,
  DynamicDashboardCardEditor,
  DynamicDashboardHeaderActions,
  DynamicDashboardSortableCard,
} from "./parts";

export const DynamicDashboardPageContent: FC = () => {
  const { entity } = usePageContext();
  const { t } = useTranslation();
  const { fetchDashboardData } = useDashboardActions();
  const [cards, setCards] = useRecoilState(dashboardCardsAtom);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overlayWidth, setOverlayWidth] = useState<number | undefined>(
    undefined,
  );
  const gridRef = useRef<HTMLDivElement>(null);

  // The Legacy cards render the real dashboard cards, which read from `dashboardDtoAtom`. Refetch
  // whenever the calendar range or the set of Legacy cards changes, but only once the layout is
  // final — see `checkIsDashboardLayoutResolved`.
  const calendarDates = useRecoilValue(
    topCalendarDatesAtomFamily(DASHBOARD_ENTITY),
  );
  // Single owner of the initial views fetch. It has to be this component and not the view panel that
  // used to own it: the aggregation request below waits for the layout, and the panel rides on
  // `customRightComponent`, which `PageHeader` only passes to the desktop header — so on mobile the
  // views would never load and the gate would never open.
  useDashboardViews({ autoLoad: true });
  const isLayoutResolved = useIsDashboardLayoutResolved();
  const [hasSettledInitialFetch, setHasSettledInitialFetch] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  const shouldRevealCards = useDashboardGridAppear(
    isLayoutResolved && hasSettledInitialFetch,
  );
  const shouldAnimateDashboard = !shouldReduceMotion;
  const appliedFilters = useRecoilValue(dashboardAppliedFiltersAtom);
  const fetchQuery = useMemo(
    () => buildDashboardFetchQuery({ calendarDates, cards, appliedFilters }),
    [calendarDates, cards, appliedFilters],
  );
  const fetchQueryKey = JSON.stringify(fetchQuery);

  useEffect(() => {
    if (!isLayoutResolved) {
      return;
    }

    if (!fetchQuery.cards?.length) {
      setHasSettledInitialFetch(true);
      return;
    }

    let isCancelled = false;

    void fetchDashboardData?.(fetchQuery)?.finally(() => {
      if (!isCancelled) {
        setHasSettledInitialFetch(true);
      }
    });

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchQueryKey captures the query
  }, [fetchQueryKey, isLayoutResolved, fetchDashboardData]);

  // Apply / clear for the filter bar (extracted to a hook) — snapshots the
  // reports' shared filter atom into the applied atom that every card reads.
  const { applyFilters, clearFilters } = useDashboardFilters();
  const headerContextValue = useMemo(
    () => ({
      onFiltersApply: applyFilters,
      onFiltersClear: clearFilters,
      onSearchApply: applyFilters,
    }),
    [applyFilters, clearFilters],
  );

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
  );

  const onDragStart = useEvent((event: DragStartEvent): void => {
    setActiveId(String(event.active.id));
    // Size the drag preview to a single grid column so a WIDE card drags just
    // like a narrow one. A full-width preview mis-measures the chart canvas at
    // mount and stretches it huge; a column-width preview renders cleanly.
    const grid = gridRef.current;
    if (grid) {
      const firstTrack = window
        .getComputedStyle(grid)
        .gridTemplateColumns.split(" ")[0];
      const width = Number.parseFloat(firstTrack);
      setOverlayWidth(Number.isNaN(width) ? undefined : width);
    }
  });

  const onDragEnd = useEvent((event: DragEndEvent): void => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) {
      return;
    }
    setCards(prev => {
      const oldIndex = prev.findIndex(c => c.id === active.id);
      const newIndex = prev.findIndex(c => c.id === over.id);
      if (oldIndex < 0 || newIndex < 0) {
        return prev;
      }
      return arrayMove(prev, oldIndex, newIndex);
    });
  });

  const cardIds = useMemo(() => cards.map(c => c.id), [cards]);
  const activeCard = useMemo(
    () => cards.find(c => c.id === activeId) ?? null,
    [cards, activeId],
  );
  // Stable narrow clone for the drag overlay — memoized so the spread doesn't
  // rebuild it (and re-render the overlay card) on every render.
  const overlayCard = useMemo(
    () => (activeCard ? { ...activeCard, size: "narrow" as const } : null),
    [activeCard],
  );

  return (
    <>
      <PageHeader
        mainTitle={t("selectListLabel.home")}
        topWidgetComponent={
          <TopHeaderCalendar
            entity={entity}
            defaultMode={TopCalendarMode.MONTH}
          />
        }
        customRightComponent={<DynamicDashboardHeaderActions />}
        customHeaderBottomLeftComponent={null}
      >
        <PageHeaderContext.Provider value={headerContextValue}>
          <DashboardFilterBar />
        </PageHeaderContext.Provider>
      </PageHeader>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <SortableContext items={cardIds} strategy={rectSortingStrategy}>
          <DashboardGridStyled
            ref={gridRef}
            variants={
              shouldAnimateDashboard
                ? DASHBOARD_GRID_APPEAR_VARIANTS
                : undefined
            }
            initial={shouldAnimateDashboard ? "hidden" : false}
            animate={
              shouldRevealCards || shouldReduceMotion ? "visible" : "hidden"
            }
          >
            {cards.map(card => (
              <DynamicDashboardSortableCard
                key={card.id}
                card={card}
                shouldAnimateAppear={shouldAnimateDashboard}
              />
            ))}
          </DashboardGridStyled>
        </SortableContext>
        <DragOverlay style={overlayWidth ? { width: overlayWidth } : undefined}>
          {overlayCard ? (
            <DynamicDashboardCard
              card={overlayCard}
              isDragClone
              shouldAnimateAppear={false}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      <DynamicDashboardCardEditor />
    </>
  );
};
